import { useSyncExternalStore } from "react";

// Unify Profiles (Figma "Unify" dashboard, Setup Baseline Profiles wizard,
// unified profile detail, Merge Logs). No backend yet: every number here is
// sample data from the design, and the setup choices are kept in this
// browser (localStorage) so the full flow can be clicked through — empty
// dashboard → setup → live dashboard. Swap for real calls once the
// endpoints exist.

export type SourceIconKey = "convohub" | "core_banking" | "kyc" | "website" | "mobile" | "ga" | "meta";

export interface UnifySource {
  id: SourceIconKey;
  name: string;
  description: string;
  records: number;
  lastSync: string;
  defaultIncluded?: boolean;
}

export const SOURCES: UnifySource[] = [
  { id: "convohub", name: "ConvoHub Data", description: "Existing and real-time customer, broadcast and automation data available to enrich your profiles", records: 30000, lastSync: "11 Oct 2026, 10:15 AM", defaultIncluded: true },
  { id: "core_banking", name: "Core Banking", description: "Customer accounts, balances, transactions, products, and core banking details.", records: 30000, lastSync: "11 Oct 2026, 10:15 AM" },
  { id: "kyc", name: "KYC Onboarding", description: "Customer verification and onboarding data from your KYC systems.", records: 30000, lastSync: "11 Oct 2026, 10:15 AM" },
  { id: "website", name: "Website SDKs", description: "Lead submissions, contact details, acquisition source, and website activity.", records: 30000, lastSync: "11 Oct 2026, 10:15 AM" },
  { id: "mobile", name: "Mobile App SDK", description: "Existing and real-time customer, broadcast and automation data available to enrich your profiles", records: 30000, lastSync: "11 Oct 2026, 10:15 AM" },
  { id: "ga", name: "Google Analytics", description: "Website sessions, page views, traffic sources, conversions, and customer behaviour signals.", records: 30000, lastSync: "11 Oct 2026, 10:15 AM" },
  { id: "meta", name: "Meta", description: "Ad impressions, clicks, conversions, audience engagement, and campaign interaction data.", records: 30000, lastSync: "11 Oct 2026, 10:15 AM" },
];

export const CONVOHUB_BREAKDOWN = [
  { value: 30000, label: "available records" },
  { value: 18420, label: "ConvoCRM contacts" },
  { value: 8320, label: "Conversation Events" },
  { value: 3260, label: "Broadcast Events" },
];

export interface ProfileField {
  id: string;
  label: string;
  type: "Text" | "Phone" | "Enum";
  primary?: boolean;
}

export const PROFILE_FIELDS: ProfileField[] = [
  { id: "customer_id", label: "CustomerID", type: "Text" },
  { id: "first_name", label: "First Name", type: "Text" },
  { id: "last_name", label: "Last Name", type: "Text" },
  { id: "email", label: "Email Address", type: "Text" },
  { id: "phone", label: "Phone Number", type: "Phone", primary: true },
  { id: "preferred_channel", label: "Preferred Channel", type: "Enum" },
];

// Columns each source exposes (sample).
export const SOURCE_FIELDS: Record<SourceIconKey, string[]> = {
  convohub: ["wa_id", "contact_id", "first_name", "last_name", "email", "phone_number", "preferred_channel"],
  core_banking: ["cif_number", "name", "first_name", "last_name", "email", "phone_number", "account_type"],
  kyc: ["user_id", "bvn", "first_name", "last_name", "email", "phone_number"],
  website: ["user_id", "anonymous_id", "email", "phone_number", "utm_source"],
  mobile: ["user_id", "device_id", "email", "phone_number", "push_token"],
  ga: ["client_id", "user_id", "session_source"],
  meta: ["meta_user_id", "email", "phone_number"],
};

// "Auto Map Fields": exact or close name matches.
const AUTO_RULES: Record<string, string[]> = {
  customer_id: ["contact_id", "cif_number", "user_id", "client_id", "meta_user_id"],
  first_name: ["first_name"],
  last_name: ["last_name"],
  email: ["email"],
  phone: ["phone_number"],
  preferred_channel: ["preferred_channel"],
};

export const autoMap = (source: SourceIconKey): Record<string, string> => {
  const fields = SOURCE_FIELDS[source];
  const out: Record<string, string> = {};
  for (const f of PROFILE_FIELDS) {
    const hit = AUTO_RULES[f.id]?.find((c) => fields.includes(c));
    if (hit) out[f.id] = hit;
  }
  return out;
};

export const FREQUENCIES = ["15 minutes", "30 minutes", "1 hour", "6 hours", "12 hours", "24 hours"];

const HOURS: Record<string, number> = { "15 minutes": 0.25, "30 minutes": 0.5, "1 hour": 1, "6 hours": 6, "12 hours": 12, "24 hours": 24 };

export const formatDateTime = (d: Date) =>
  d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).replace(" am", " AM").replace(" pm", " PM");

export const nextRefresh = (frequency: string, from = new Date()) => formatDateTime(new Date(from.getTime() + (HOURS[frequency] || 1) * 3600_000));

// ── Setup state (browser-only until there's an API) ─────────────────────────

export interface UnifySetup {
  selected: SourceIconKey[];
  mapping: Partial<Record<SourceIconKey, Record<string, string>>>;
  frequency: Partial<Record<SourceIconKey, string>>;
  step: number;
  generatedAt: string | null;
}

const KEY = "unify_baseline_setup";
const DEFAULTS: UnifySetup = {
  selected: ["convohub", "core_banking", "kyc", "website", "mobile", "ga", "meta"],
  mapping: { convohub: { first_name: "first_name", last_name: "last_name", email: "wa_id", phone: "phone_number", preferred_channel: "preferred_channel" } },
  frequency: {},
  step: 0,
  generatedAt: null,
};

const load = (): UnifySetup => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return DEFAULTS;
  }
};

let state = load();
const listeners = new Set<() => void>();

export const updateUnifySetup = (patch: Partial<UnifySetup>) => {
  state = { ...state, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — kept for this session */
  }
  listeners.forEach((l) => l());
};

export const resetUnifySetup = () => updateUnifySetup({ ...DEFAULTS });

export const useUnifySetup = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state
  );

// Mapping status per profile field across the selected sources.
export type MapStatus = "Fully Mapped" | "Partially Mapped" | "Unmapped";

export const fieldStatus = (s: UnifySetup, fieldId: string): MapStatus => {
  const mapped = s.selected.filter((src) => s.mapping[src]?.[fieldId]).length;
  if (mapped === 0) return "Unmapped";
  return mapped === s.selected.length ? "Fully Mapped" : "Partially Mapped";
};

export const mappingCounts = (s: UnifySetup) => {
  const statuses = PROFILE_FIELDS.map((f) => fieldStatus(s, f.id));
  return {
    full: statuses.filter((x) => x === "Fully Mapped").length,
    partial: statuses.filter((x) => x === "Partially Mapped").length,
    unmapped: statuses.filter((x) => x === "Unmapped").length,
  };
};

export const selectedRecords = (s: UnifySetup) => SOURCES.filter((src) => s.selected.includes(src.id)).reduce((n, src) => n + src.records, 0);

export const enrichmentCoverage = (s: UnifySetup) => {
  const cells = s.selected.length * PROFILE_FIELDS.length;
  if (!cells) return 0;
  const filled = s.selected.reduce((n, src) => n + PROFILE_FIELDS.filter((f) => s.mapping[src]?.[f.id]).length, 0);
  return Math.round((filled / cells) * 100);
};

// ── Sample profiles / runs / logs ───────────────────────────────────────────

export const DASHBOARD_STATS = {
  available: 30000,
  unified: 23393,
  enriched: 94820,
  sources: 4,
  matchRate: 87,
  unmatched: 1580,
  lastRun: { at: "11 Oct 2026, 10:15 AM", next: "12 Oct 2026, 10:15 AM", processed: 99520, enriched: 24, matchRate: 67, unmatched: 12 },
  native: { crm: 99520, broadcast: 56, conversation: 24, automation: 12 },
  health: [
    { label: "Anonymous IDs", pct: 82, count: 1238, color: "#9CA3C9" },
    { label: "Fully Resolved", pct: 5, count: 90000, color: "#16A34A" },
    { label: "Enriched Profiles", pct: 12, count: 7256, color: "#4338CA" },
  ],
};

export interface UnifiedProfile {
  id: string;
  name: string;
  phone: string;
  avatar?: string;
  kyc: string;
  country: string;
  cif: string;
  whatsapp: string;
  email: string;
  gender: string;
  ageGroup: string;
  interests: string[];
  location: string;
  incomeBand: string;
  creditScore: number;
  accountType: string;
  coverage: number;
  sources: number;
  updated: string;
  traits: { declared: [string, string[]][]; behavioural: [string, string[]][]; predictive: [string, string][] };
  activity: string[];
  externalIds: { type: string; value: string; source: string; added: string }[];
  merges: { at: string; source: string; rule: string; action: string }[];
  segments: string[];
}

const baseProfile = (id: string, name: string, phone: string, email: string): UnifiedProfile => ({
  id,
  name,
  phone,
  kyc: "KYC Verified",
  country: "Nigeria",
  cif: "CS-83947494",
  whatsapp: phone,
  email,
  gender: "Male",
  ageGroup: "18-24",
  interests: ["Movies"],
  location: "Bengaluru, K.A.",
  incomeBand: "High Income Band",
  creditScore: 780,
  accountType: "Individual",
  coverage: 70,
  sources: 3,
  updated: "Updated 2 hours ago",
  traits: {
    declared: [["Occupation Category", ["Self-Employed"]]],
    behavioural: [["Products signed up", ["Savings account", "Debit Card", "Personal Loan"]]],
    predictive: [["Dormancy Risk Score", "57"]],
  },
  activity: ["Sent First WhatsApp Message", "Product Enquiry", "Confirmed First Order", "Shared first feedback", "Sent Second Message"],
  externalIds: [
    { type: "Mobile App User ID", value: "app_user_99", source: "Meridian App", added: "12 Oct, 2026" },
    { type: "Website ID", value: "app_user_99", source: "Website", added: "12 Oct, 2026" },
    { type: "Device ID", value: "app_user_99", source: "Website", added: "12 Oct, 2026" },
    { type: "Meta User ID", value: "app_user_99", source: "Meridian App", added: "12 Oct, 2026" },
    { type: "Loan System ID", value: "app_user_99", source: "Loan Management App", added: "12 Oct, 2026" },
  ],
  merges: [
    { at: "12 Sep, 2026, 10:24AM", source: "Core Banking", rule: "CIF", action: "Merged" },
    { at: "12 Sep, 2026, 10:24AM", source: "KYC Onboarding", rule: "Device ID", action: "Linked" },
    { at: "12 Sep, 2026, 10:24AM", source: "Website Leads", rule: "CIF", action: "Enriched" },
  ],
  segments: ["High Net-Worth Individuals", "Top Spenders"],
});

export const PROFILES: UnifiedProfile[] = [
  { ...baseProfile("john-doe", "John Doe", "+91-9900990099", "johndoe@gmail.com") },
  { ...baseProfile("karen-castillo", "Karen Castillo", "+91-9788882888", "karencastillo@gmail.com"), gender: "Female" },
  { ...baseProfile("tabitha-potter", "Tabitha Potter", "+91-9123456780", "tabitha.potter@gmail.com"), gender: "Female" },
  { ...baseProfile("maximillian-jacob", "Maximillian Jacob", "+91-9988776655", "max.jacob@gmail.com") },
];

export const findProfile = (id?: string) => PROFILES.find((p) => p.id === id);

export interface MergeLog {
  at: string;
  source: string;
  profile: string;
  rule: string;
  action: "Merged" | "Enriched" | "Linked";
  result: "Success" | "No Matches";
}

export const MERGE_LOGS: MergeLog[] = [
  { at: "12 Sep, 2026, 10:24AM", source: "Core Banking", profile: "John Doe", rule: "CIF", action: "Merged", result: "Success" },
  { at: "12 Sep, 2026, 10:24AM", source: "ConvoHub CRM", profile: "Karen Castillo", rule: "Mobile number", action: "Enriched", result: "No Matches" },
  { at: "12 Sep, 2026, 10:24AM", source: "KYC Onboarding", profile: "Tabitha Potter", rule: "Device ID", action: "Linked", result: "No Matches" },
  { at: "12 Sep, 2026, 10:24AM", source: "Mobile App SDKs", profile: "Maximillian Jacob", rule: "CIF", action: "Enriched", result: "No Matches" },
  { at: "12 Sep, 2026, 10:24AM", source: "Website Leads", profile: "John Doe", rule: "CIF", action: "Enriched", result: "Success" },
];
